/**
 * @file AvitoReviewsPage.tsx
 * @description Список отзывов с Avito (phase14.0).
 * UX: toolbar с кнопками + empty-state с подсказкой.
 */

import { Loader2, Package, RefreshCw, Star } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router";

import { Alert } from "../../../components/ui/alert";
import { Button } from "../../../components/ui/button";
import { EditorialCard } from "../../../components/ui/EditorialCard";
import { useNotify } from "../../../components/ui/NotifyProvider";
import { ApiError } from "../../../lib/api-client";
import { shopAvito, type AvitoReviewDto } from "../../../lib/api-shop-avito";

export function AvitoReviewsPage() {
  const notify = useNotify();
  const { slug } = useParams<{ slug: string }>();
  const [items, setItems] = useState<AvitoReviewDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await shopAvito.reviews({ page: 1, perPage: 100 });
      setItems(r.items);
      setTotal(r.meta.total);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить отзывы");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function syncNow(): Promise<void> {
    setSyncing(true);
    try {
      const r = await shopAvito.triggerSync("reviews");
      if (r.status === "success") {
        notify.success("Синхронизация отзывов завершена");
        await load();
      } else {
        notify.error("Синхронизация завершена с ошибкой — см. журнал");
      }
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось запустить синхру");
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-on-surface-variant">
          Всего отзывов в кэше: <strong className="text-on-surface">{total}</strong>
        </p>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={load} disabled={loading || syncing}>
            <RefreshCw className="size-4" />
            Обновить
          </Button>
          <Button onClick={syncNow} disabled={syncing} size="sm">
            {syncing ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
            Синхронизировать с Avito
          </Button>
        </div>
      </div>

      {loading ? (
        <Loader2 className="size-5 animate-spin mx-auto my-16 text-on-surface-variant" />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : items.length === 0 ? (
        <EditorialCard>
          <EditorialCard.Body className="text-center py-10 space-y-3">
            <p className="text-base font-medium text-on-surface">Отзывов нет</p>
            <p className="text-sm text-on-surface-variant max-w-md mx-auto">
              Отзывы появятся после синхронизации с Avito API. Cron делает это раз в сутки.
            </p>
            <Button onClick={syncNow} disabled={syncing}>
              {syncing ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <RefreshCw className="size-4" />
              )}
              Синхронизировать сейчас
            </Button>
          </EditorialCard.Body>
        </EditorialCard>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {items.map((r) => (
            <EditorialCard key={r.id}>
              <EditorialCard.Body className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-on-surface">{r.senderName}</span>
                    <Stars score={r.score} />
                  </div>
                  <span className="text-xs text-on-surface-variant">
                    {new Date(r.createdAtAvito).toLocaleDateString("ru-RU")}
                  </span>
                </div>
                {r.product ? (
                  <div className="text-xs text-on-surface-variant flex items-center gap-1.5">
                    <Package className="size-3.5" />
                    <span>Товар:</span>
                    <Link
                      to={`/shop/${slug}/avito/listings/${r.product.id}`}
                      className="text-primary hover:underline"
                    >
                      {r.product.displayName}
                    </Link>
                  </div>
                ) : (
                  <div className="text-xs text-on-surface-variant italic">
                    Товар не определён (не удалось смапить avitoItemId)
                  </div>
                )}
                {r.text && (
                  <p className="text-sm text-on-surface whitespace-pre-line">{r.text}</p>
                )}
              </EditorialCard.Body>
            </EditorialCard>
          ))}
        </div>
      )}
    </div>
  );
}

function Stars({ score }: { score: number }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${score} из 5`}>
      {Array.from({ length: 5 }, (_, i) => (
        <Star
          key={i}
          className={
            i < score
              ? "size-3.5 fill-warning text-warning"
              : "size-3.5 text-on-surface-variant"
          }
        />
      ))}
    </span>
  );
}
